import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useToast } from '@/components/ui/use-toast'
import {
  useGetPublicOrgInvitationQuery,
  useAcceptOrgInvitationMutation,
} from '@/redux/marketerApi'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCurrentUser } from '@/redux/userSlice'
import type { AppDispatch, RootState } from '@/store'

const AcceptOrgInviteModal = () => {
  const [token, setToken] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const currentUser = useSelector((state: RootState) => state.user.currentUser)

  useEffect(() => {
    if (!currentUser?.id) return
    const savedToken = localStorage.getItem('pendingOrgInviteToken')
    if (savedToken) {
      setToken(savedToken)
      setIsOpen(true)
    }
  }, [currentUser?.id])

  const { data: invite, isLoading: isFetchingInvite } = useGetPublicOrgInvitationQuery(
    token as string,
    { skip: !token || !isOpen },
  )

  const [acceptInvite, { isLoading: isAccepting }] = useAcceptOrgInvitationMutation()

  const handleDecline = () => {
    localStorage.removeItem('pendingOrgInviteToken')
    setToken(null)
    setIsOpen(false)
  }

  const handleAccept = async () => {
    if (!token) return
    try {
      await acceptInvite(token).unwrap()
      toast({
        title: 'Welcome to the organization!',
        description: `You have successfully joined ${invite?.org?.name || 'the organization'}.`,
      })

      localStorage.removeItem('pendingOrgInviteToken')
      setToken(null)
      setIsOpen(false)

      // Refresh user state so the new org context is reflected
      dispatch(fetchCurrentUser())
      navigate('/')
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.data?.error || 'Failed to accept the invitation. It may have expired.',
        variant: 'destructive',
      })
      localStorage.removeItem('pendingOrgInviteToken')
      setIsOpen(false)
    }
  }

  if (!isOpen || !token) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleDecline}>
      <DialogContent className="sm:max-w-md bg-card border-foreground/10 text-foreground z-60">
        <DialogHeader>
          <DialogTitle className="text-xl">Accept Organization Invitation</DialogTitle>
          <DialogDescription className="text-gray-600">
            You've been invited to join an organization on Athios.
          </DialogDescription>
        </DialogHeader>

        {isFetchingInvite ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#C0FD72]" />
          </div>
        ) : !invite ? (
          <div className="py-6 text-center text-gray-600">Loading invitation details...</div>
        ) : (
          <div className="py-6 flex flex-col items-center justify-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={invite.inviter?.avatar || undefined} />
              <AvatarFallback className="bg-[#C0FD72] text-black">
                {invite.inviter?.name?.charAt(0) || '?'}
              </AvatarFallback>
            </Avatar>

            <div className="text-center">
              <p className="text-foreground text-lg">
                <strong className="text-[#3F7A0A]">
                  {invite.inviter?.name || 'A team member'}
                </strong>{' '}
                wants you to join{' '}
                <strong className="text-foreground">
                  {invite.org?.name || 'their organization'}
                </strong>
                .
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="sm:justify-between gap-3 sm:gap-0 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleDecline}
            disabled={isAccepting}
            className="bg-transparent border-foreground/10 text-foreground hover:bg-foreground/5"
          >
            Decline
          </Button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={isAccepting || isFetchingInvite || !invite}
            className="bg-[#C0FD72] text-black hover:bg-[#a8e45a]"
          >
            {isAccepting ? 'Accepting...' : 'Accept Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AcceptOrgInviteModal
